import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Mail, Download, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportRows } from '../../lib/csv';

function ExportBtn({ onClick }) {
  return (
    <button onClick={onClick} data-testid="export-csv" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 h-10 text-xs font-semibold inline-flex items-center gap-2">
      <Download className="w-3.5 h-3.5" /> Excel'e Aktar
    </button>
  );
}

const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR') : '';

export function AdminDonations() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const reload = () => api.adminDonations().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []);
  const total = list.reduce((s, d) => s + (d.amount || 0), 0);
  const totalBy = (st) => list.filter(d => (d.status || 'pending') === st).reduce((s, d) => s + (d.amount || 0), 0);
  const methodOf = (d) => d.method || ((d.card_type || '').includes('iyzico') ? 'card' : (d.note === 'Havale/EFT bildirimi' ? 'havale' : 'card'));
  const setStatus = async (id, status) => {
    try {
      await api.adminUpdateDonationStatus(id, status);
      toast.success(status === 'completed' ? 'Bağış onaylandı' : status === 'rejected' ? 'Bağış reddedildi' : 'Durum güncellendi');
      setOpen(null);
      reload();
    } catch { toast.error('Durum güncellenemedi'); }
  };
  const removeDonation = async (id) => {
    if (!window.confirm('Bu bağış kaydı kalıcı olarak silinecek. Emin misiniz?')) return;
    try {
      await api.adminDeleteDonation(id);
      toast.success('Kayıt silindi');
      setOpen(null);
      reload();
    } catch { toast.error('Silinemedi'); }
  };
  const exportCsv = () => {
    exportRows(`bagislar-${new Date().toISOString().slice(0,10)}.csv`, list.map(d => ({
      ...d,
      created_at: fmtDate(d.created_at),
      paid_at: fmtDate(d.paid_at),
      method: methodOf(d) === 'havale' ? 'Havale/EFT' : 'Kredi Kartı',
      bank_name: d.bank?.bank, bank_iban: d.bank?.iban,
      info_name: d.info?.name, info_surname: d.info?.surname,
      info_email: d.info?.email, info_phone: d.info?.phone, info_city: d.info?.city,
      card_recipient: d.card_data?.toName, card_email: d.card_data?.toEmail, card_occasion: d.card_data?.occasion_label,
    })), [
      { key: 'created_at', label: 'Tarih' },
      { key: 'info_name', label: 'Ad' }, { key: 'info_surname', label: 'Soyad' },
      { key: 'info_email', label: 'E-posta' }, { key: 'info_phone', label: 'Telefon' }, { key: 'info_city', label: 'Şehir' },
      { key: 'amount', label: 'Tutar (₺)' }, { key: 'method', label: 'Yöntem' },
      { key: 'bank_name', label: 'Banka' }, { key: 'bank_iban', label: 'IBAN' },
      { key: 'card_type', label: 'Tür' },
      { key: 'card_occasion', label: 'Özel Gün' }, { key: 'card_recipient', label: 'Alıcı Adı' }, { key: 'card_email', label: 'Alıcı E-posta' },
      { key: 'status', label: 'Durum' }, { key: 'paid_at', label: 'Ödeme Tarihi' }, { key: 'note', label: 'Not' },
    ]);
    toast.success(`${list.length} bağış aktarıldı`);
  };
  return (
    <>
    <Section title="Bağışlar" kicker="Başvurular"
      subtitle={(
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>{list.length} bağış · Toplam ₺{total.toLocaleString('tr-TR')}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600" data-testid="total-approved">Onaylanan: ₺{totalBy('completed').toLocaleString('tr-TR')}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gold/15 text-gold-500" data-testid="total-pending">Bekleyen: ₺{totalBy('pending').toLocaleString('tr-TR')}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-coral-50 text-coral-300" data-testid="total-rejected">Reddedilen: ₺{totalBy('rejected').toLocaleString('tr-TR')}</span>
        </span>
      )}
      action={<ExportBtn onClick={exportCsv} />}>
      <Table cols={['Tarih', 'Bağışçı', 'E-posta', 'Tutar', 'Yöntem', 'Banka', 'Durum', '']}>
        {list.map(d => {
          const m = methodOf(d);
          return (
          <tr key={d.id} className="border-t border-ink-50 cursor-pointer hover:bg-cream-200" onClick={() => setOpen(d)} data-testid={`donation-row-${d.id}`}>
            <td className="p-3 text-xs tabular text-ink-500">{fmtDate(d.created_at)}</td>
            <td className="p-3 text-sm font-semibold">{d.info?.name} {d.info?.surname || ''}</td>
            <td className="p-3 text-xs text-ink-500">{d.info?.email}</td>
            <td className="p-3 font-display text-base tabular">₺{(d.amount || 0).toLocaleString('tr-TR')}</td>
            <td className="p-3"><Badge tone={m === 'havale' ? 'gold' : 'navy'}>{m === 'havale' ? 'Havale/EFT' : 'Kredi Kartı'}</Badge></td>
            <td className="p-3 text-xs text-ink-500">{d.bank?.bank || '-'}</td>
            <td className="p-3"><DonationStatusBadge status={d.status} /></td>
            <td className="p-3 text-right whitespace-nowrap">
              {m === 'havale' && d.status === 'pending' && (
                <span className="inline-flex gap-1.5">
                  <button onClick={(e) => { e.stopPropagation(); setStatus(d.id, 'completed'); }} data-testid={`approve-${d.id}`}
                    className="bg-emerald-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-emerald-700">Onayla</button>
                  <button onClick={(e) => { e.stopPropagation(); setStatus(d.id, 'rejected'); }} data-testid={`reject-${d.id}`}
                    className="bg-coral-300 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-coral-400">Reddet</button>
                </span>
              )}
              <button onClick={(e) => { e.stopPropagation(); setOpen(d); }} data-testid={`donation-detail-btn-${d.id}`}
                className="ml-1.5 inline-flex items-center gap-1 bg-ink-600 text-cream rounded-full px-3 h-8 text-xs font-semibold hover:bg-ink-500"><Eye className="w-3 h-3" /> Detay</button>
              <button onClick={(e) => { e.stopPropagation(); removeDonation(d.id); }} data-testid={`donation-delete-btn-${d.id}`}
                title="Kaydı sil (sahte talep)"
                className="ml-1.5 inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-red-700"><Trash2 className="w-3 h-3" /> Sil</button>
            </td>
          </tr>
          );
        })}
        {list.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-ink-400">Henüz bağış yok</td></tr>}
      </Table>
    </Section>
    {open && (
      <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(null)}>
        <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="donation-detail-modal">
          <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Bağış Detayı</div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-display text-2xl mt-1">{open.info?.name} {open.info?.surname || ''} · ₺{(open.amount || 0).toLocaleString('tr-TR')}</h2>
            <DonationStatusBadge status={open.status} />
          </div>
          <div className="mt-1 text-xs text-ink-400 tabular">{fmtDate(open.created_at)}</div>
          <div className="mt-5 divide-y divide-ink-50 border border-ink-100 rounded-xl overflow-hidden text-sm">
            <DetailRow k="Ödeme Yöntemi" v={methodOf(open) === 'havale' ? 'Havale / EFT' : 'Kredi Kartı (iyzico)'} strong />
            {open.bank?.bank && <DetailRow k="Seçilen Banka" v={`${open.bank.bank} (${open.bank.currency || 'TL'})`} strong />}
            {open.bank?.iban && <DetailRow k="IBAN" v={open.bank.iban} mono />}
            <DetailRow k="Tutar" v={`₺${(open.amount || 0).toLocaleString('tr-TR')}${open.recurring ? ' / ay (düzenli)' : ' (tek seferlik)'}`} />
            <DetailRow k="E-posta" v={open.info?.email} />
            {open.info?.phone && <DetailRow k="Telefon" v={open.info.phone} />}
            {open.info?.city && <DetailRow k="Şehir" v={open.info.city} />}
            {open.note && <DetailRow k="Not" v={open.note} />}
            {open.card_type && <DetailRow k="Tür" v={open.card_type} />}
          </div>
          {methodOf(open) === 'havale' && open.status === 'pending' && (
            <div className="mt-4 p-4 rounded-xl bg-gold/10 border border-gold/30 text-xs text-ink-600">
              Bu bağış bir <strong>havale bildirimi</strong>dir. Yukarıdaki banka hesabınızı kontrol edip ödeme ulaştıysa onaylayın; sahte/geçersiz bildirimleri reddedin.
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2 flex-wrap">
            {methodOf(open) === 'havale' && open.status === 'pending' && (
              <>
                <button onClick={() => setStatus(open.id, 'completed')} data-testid="modal-approve" className="bg-emerald-600 text-white rounded-full px-5 h-11 text-sm font-semibold hover:bg-emerald-700">✓ Ödemeyi Onayla</button>
                <button onClick={() => setStatus(open.id, 'rejected')} data-testid="modal-reject" className="bg-coral-300 text-white rounded-full px-5 h-11 text-sm font-semibold hover:bg-coral-400">Reddet</button>
              </>
            )}
            {open.status !== 'pending' && methodOf(open) === 'havale' && (
              <button onClick={() => setStatus(open.id, 'pending')} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold">Beklemeye Al</button>
            )}
            <button onClick={() => removeDonation(open.id)} data-testid="modal-delete" className="px-5 h-11 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Sil</button>
            <button onClick={() => setOpen(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold">Kapat</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function DonationStatusBadge({ status }) {
  if (status === 'completed') return <Badge tone="emerald">Onaylandı</Badge>;
  if (status === 'rejected') return <Badge tone="coral">Reddedildi</Badge>;
  return <Badge tone="gold">Onay Bekliyor</Badge>;
}

function DetailRow({ k, v, mono, strong }) {
  return (
    <div className="grid grid-cols-3 gap-3 p-3">
      <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold">{k}</div>
      <div className={`col-span-2 break-words ${mono ? 'font-mono tabular' : ''} ${strong ? 'font-semibold text-ink-600' : 'text-ink-600'}`}>{v}</div>
    </div>
  );
}

export function AdminScholarships() {
  const [list, setList] = useState([]);
  const reload = () => api.adminScholarships().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []);
  const setStatus = async (id, st) => {
    await api.adminUpdateScholarshipStatus(id, st);
    toast.success('Durum güncellendi'); reload();
  };
  const exportCsv = () => {
    exportRows(`burs-basvurulari-${new Date().toISOString().slice(0,10)}.csv`, list.map(s => ({ ...s, created_at: fmtDate(s.created_at) })), [
      { key: 'created_at', label: 'Tarih' }, { key: 'name', label: 'Ad Soyad' }, { key: 'tc', label: 'TC' },
      { key: 'phone', label: 'Telefon' }, { key: 'email', label: 'E-posta' },
      { key: 'university', label: 'Üniversite' }, { key: 'department', label: 'Bölüm' }, { key: 'year', label: 'Sınıf' },
      { key: 'income', label: 'Gelir' }, { key: 'reason', label: 'Sebep' }, { key: 'status', label: 'Durum' },
    ]);
    toast.success(`${list.length} başvuru aktarıldı`);
  };
  return (
    <Section title="Burs Başvuruları" kicker="Başvurular" subtitle={`${list.length} başvuru`} action={<ExportBtn onClick={exportCsv} />}>
      <Table cols={['Tarih', 'Aday', 'E-posta', 'Üniversite', 'Durum', 'İşlem']}>
        {list.map(s => (
          <tr key={s.id} className="border-t border-ink-50">
            <td className="p-3 text-xs tabular text-ink-500">{fmtDate(s.created_at)}</td>
            <td className="p-3 text-sm font-semibold">{s.name}</td>
            <td className="p-3 text-xs text-ink-500">{s.email}<br /><span className="text-ink-400">{s.phone}</span></td>
            <td className="p-3 text-sm">{s.university}<br /><span className="text-xs text-ink-400">{s.department} · {s.year}. sınıf</span></td>
            <td className="p-3"><Badge tone={statusTone(s.status)}>{s.status || 'new'}</Badge></td>
            <td className="p-3">
              <select value={s.status || 'new'} onChange={e => setStatus(s.id, e.target.value)} className="bg-cream-200 border border-ink-100 rounded-lg px-2 h-8 text-xs">
                <option value="new">Yeni</option>
                <option value="reviewing">İnceleniyor</option>
                <option value="approved">Onaylandı</option>
                <option value="rejected">Reddedildi</option>
              </select>
            </td>
          </tr>
        ))}
        {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ink-400">Henüz başvuru yok</td></tr>}
      </Table>
    </Section>
  );
}

export function AdminVolunteers() {
  const [list, setList] = useState([]);
  const [trash, setTrash] = useState([]);
  const [view, setView] = useState('all'); // all | new | approved | rejected | trash
  const [open, setOpen] = useState(null);
  const reload = () => {
    api.adminVolunteers().then(setList).catch(() => setList([]));
    api.adminVolunteersTrash().then(setTrash).catch(() => setTrash([]));
  };
  useEffect(() => { reload(); }, []);
  const SKIP_KEYS = ['id', '_id', 'created_at', 'type', 'status', 'deleted_at', 'photo'];
  const stOf = (v) => v.status || 'new';
  const filtered = view === 'trash' ? trash : (view === 'all' ? list : list.filter(v => stOf(v) === view));
  const counts = {
    all: list.length,
    new: list.filter(v => stOf(v) === 'new').length,
    approved: list.filter(v => stOf(v) === 'approved').length,
    rejected: list.filter(v => stOf(v) === 'rejected').length,
    trash: trash.length,
  };
  const setStatus = async (id, status) => {
    try {
      await api.adminVolunteerStatus(id, status);
      toast.success(status === 'approved' ? 'Başvuru onaylandı' : status === 'rejected' ? 'Başvuru reddedildi' : 'Durum güncellendi');
      setOpen(null); reload();
    } catch { toast.error('Durum güncellenemedi'); }
  };
  const toTrash = async (id) => {
    if (!window.confirm('Başvuru çöp kutusuna taşınacak ve 30 gün sonra otomatik silinecek. Devam edilsin mi?')) return;
    try { await api.adminVolunteerDelete(id); toast.success('Çöp kutusuna taşındı (30 gün saklanır)'); setOpen(null); reload(); }
    catch { toast.error('Silinemedi'); }
  };
  const restore = async (id) => {
    try { await api.adminVolunteerRestore(id); toast.success('Başvuru geri alındı'); reload(); }
    catch { toast.error('Geri alınamadı'); }
  };
  const purge = async (id) => {
    if (!window.confirm('Bu kayıt KALICI olarak silinecek. Emin misiniz?')) return;
    try { await api.adminVolunteerPurge(id); toast.success('Kalıcı olarak silindi'); reload(); }
    catch { toast.error('Silinemedi'); }
  };
  const daysLeft = (v) => {
    if (!v.deleted_at) return '';
    const end = new Date(v.deleted_at).getTime() + 30 * 86400000;
    const d = Math.max(0, Math.ceil((end - Date.now()) / 86400000));
    return `${d} gün kaldı`;
  };
  const exportCsv = () => {
    const dynKeys = Array.from(new Set(list.flatMap(v => Object.keys(v)))).filter(k => !SKIP_KEYS.includes(k));
    exportRows(`gonulluler-${new Date().toISOString().slice(0,10)}.csv`, list.map(v => ({ ...v, created_at: fmtDate(v.created_at) })), [
      { key: 'created_at', label: 'Tarih' }, { key: 'type', label: 'Tip' }, { key: 'status', label: 'Durum' },
      ...dynKeys.map(k => ({ key: k, label: k })),
    ]);
    toast.success(`${list.length} gönüllü aktarıldı`);
  };
  const TABS = [
    { key: 'all', label: 'Tümü' }, { key: 'new', label: 'Yeni' },
    { key: 'approved', label: 'Onaylanan' }, { key: 'rejected', label: 'Reddedilen' },
    { key: 'trash', label: 'Çöp Kutusu' },
  ];
  return (
    <>
    <Section title="Başvuru Formları" kicker="Başvurular" subtitle={`${list.length} kayıt · Detay için satıra tıklayın · Silinenler çöp kutusunda 30 gün saklanır`} action={<ExportBtn onClick={exportCsv} />}>
      <div className="p-3 flex flex-wrap gap-2 border-b border-ink-50 bg-cream-200/40">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setView(t.key)} data-testid={`vol-tab-${t.key}`}
            className={`px-4 h-9 rounded-full text-xs font-semibold border transition-colors ${view === t.key ? 'bg-ink-600 text-cream border-ink-600' : 'bg-white text-ink-500 border-ink-100 hover:border-ink-300'}`}>
            {t.label} ({counts[t.key]})
          </button>
        ))}
      </div>
      <Table cols={view === 'trash' ? ['Tarih', 'Tip', 'İsim/Şirket', 'E-posta', 'Süre', ''] : ['Tarih', 'Tip', 'İsim/Şirket', 'E-posta', 'Telefon', 'Durum', '']}>
        {view !== 'trash' && filtered.map(v => (
          <tr key={v.id} className="border-t border-ink-50 cursor-pointer hover:bg-cream-200" onClick={() => setOpen(v)} data-testid={`volunteer-row-${v.id}`}>
            <td className="p-3 text-xs tabular text-ink-500">{fmtDate(v.created_at)}</td>
            <td className="p-3"><Badge tone={v.type === 'kurumsal' ? 'gold' : 'navy'}>{v.type}</Badge></td>
            <td className="p-3 text-sm font-semibold">{v.name || v.company}</td>
            <td className="p-3 text-xs text-ink-500">{v.email}</td>
            <td className="p-3 text-xs text-ink-500">{v.phone}</td>
            <td className="p-3"><VolStatusBadge status={stOf(v)} /></td>
            <td className="p-3 text-right whitespace-nowrap">
              {stOf(v) === 'new' && (
                <span className="inline-flex gap-1.5">
                  <button onClick={(e) => { e.stopPropagation(); setStatus(v.id, 'approved'); }} data-testid={`vol-approve-${v.id}`}
                    className="bg-emerald-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-emerald-700">Onayla</button>
                  <button onClick={(e) => { e.stopPropagation(); setStatus(v.id, 'rejected'); }} data-testid={`vol-reject-${v.id}`}
                    className="bg-coral-300 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-coral-400">Reddet</button>
                </span>
              )}
              <button onClick={(e) => { e.stopPropagation(); setOpen(v); }} data-testid={`volunteer-detail-btn-${v.id}`}
                className="ml-1.5 inline-flex items-center gap-1 bg-ink-600 text-cream rounded-full px-3 h-8 text-xs font-semibold hover:bg-ink-500"><Eye className="w-3 h-3" /> Detay</button>
              <button onClick={(e) => { e.stopPropagation(); toTrash(v.id); }} data-testid={`vol-delete-${v.id}`}
                title="Çöp kutusuna taşı (30 gün saklanır)"
                className="ml-1.5 inline-flex items-center gap-1 bg-red-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-red-700"><Trash2 className="w-3 h-3" /> Sil</button>
            </td>
          </tr>
        ))}
        {view === 'trash' && filtered.map(v => (
          <tr key={v.id} className="border-t border-ink-50 hover:bg-cream-200" data-testid={`trash-row-${v.id}`}>
            <td className="p-3 text-xs tabular text-ink-500">{fmtDate(v.created_at)}</td>
            <td className="p-3"><Badge tone={v.type === 'kurumsal' ? 'gold' : 'navy'}>{v.type}</Badge></td>
            <td className="p-3 text-sm font-semibold">{v.name || v.company}</td>
            <td className="p-3 text-xs text-ink-500">{v.email}</td>
            <td className="p-3 text-xs text-red-500 font-semibold">{daysLeft(v)}</td>
            <td className="p-3 text-right whitespace-nowrap">
              <button onClick={() => restore(v.id)} data-testid={`vol-restore-${v.id}`}
                className="bg-emerald-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-emerald-700">Geri Al</button>
              <button onClick={() => purge(v.id)} data-testid={`vol-purge-${v.id}`}
                className="ml-1.5 bg-red-600 text-white rounded-full px-3 h-8 text-xs font-semibold hover:bg-red-700">Kalıcı Sil</button>
            </td>
          </tr>
        ))}
        {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-ink-400">{view === 'trash' ? 'Çöp kutusu boş' : 'Bu görünümde başvuru yok'}</td></tr>}
      </Table>
    </Section>
    {open && (
      <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(null)}>
        <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="volunteer-detail-modal">
          <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Gönüllü Başvurusu · {open.type}</div>
          <div className="mt-2 flex items-center gap-4">
            {open.photo && (
              <img src={open.photo} alt={open.name || 'Başvuran'} data-testid="volunteer-photo-preview"
                className="w-16 h-16 rounded-full object-cover border-2 border-gold shrink-0" />
            )}
            <div className="flex-1 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-display text-2xl">{open.name || open.company || '(isimsiz)'}</h2>
              <VolStatusBadge status={stOf(open)} />
            </div>
          </div>
          <div className="mt-1 text-xs text-ink-400 tabular">{fmtDate(open.created_at)}</div>
          <div className="mt-5 divide-y divide-ink-50 border border-ink-100 rounded-xl overflow-hidden">
            {Object.entries(open).filter(([k, v]) => !SKIP_KEYS.includes(k) && String(v || '').trim()).map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-3 p-3 text-sm">
                <div className="text-xs uppercase tracking-wider text-ink-400 font-semibold break-words">{k}</div>
                <div className="col-span-2 text-ink-600 whitespace-pre-wrap break-words">{String(v)}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex justify-end gap-2 flex-wrap">
            {stOf(open) !== 'approved' && (
              <button onClick={() => setStatus(open.id, 'approved')} data-testid="vol-modal-approve" className="bg-emerald-600 text-white rounded-full px-5 h-11 text-sm font-semibold hover:bg-emerald-700">✓ Onayla</button>
            )}
            {stOf(open) !== 'rejected' && (
              <button onClick={() => setStatus(open.id, 'rejected')} data-testid="vol-modal-reject" className="bg-coral-300 text-white rounded-full px-5 h-11 text-sm font-semibold hover:bg-coral-400">Reddet</button>
            )}
            {stOf(open) !== 'new' && (
              <button onClick={() => setStatus(open.id, 'new')} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold">Yeni Olarak İşaretle</button>
            )}
            <button onClick={() => toTrash(open.id)} data-testid="vol-modal-delete" className="px-5 h-11 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Sil</button>
            {open.email && <a href={`mailto:${open.email}`} className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold inline-flex items-center gap-2"><Mail className="w-4 h-4" /> Yanıtla</a>}
            <button onClick={() => setOpen(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold">Kapat</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function VolStatusBadge({ status }) {
  if (status === 'approved') return <Badge tone="emerald">Onaylandı</Badge>;
  if (status === 'rejected') return <Badge tone="coral">Reddedildi</Badge>;
  return <Badge tone="gold">Yeni</Badge>;
}

export function AdminMessages() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const reload = () => api.adminMessages().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []);
  const markRead = async (id) => { await api.adminMarkRead(id); reload(); };
  const exportCsv = () => {
    exportRows(`mesajlar-${new Date().toISOString().slice(0,10)}.csv`, list.map(m => ({ ...m, created_at: fmtDate(m.created_at), read: m.read ? 'Okundu' : 'Yeni' })), [
      { key: 'created_at', label: 'Tarih' }, { key: 'name', label: 'Ad Soyad' },
      { key: 'email', label: 'E-posta' }, { key: 'subject', label: 'Konu' },
      { key: 'message', label: 'Mesaj' }, { key: 'read', label: 'Durum' },
    ]);
    toast.success(`${list.length} mesaj aktarıldı`);
  };
  return (
    <>
      <Section title="Mesajlar" kicker="Başvurular" subtitle={`${list.length} mesaj · ${list.filter(m => !m.read).length} okunmamış`} action={<ExportBtn onClick={exportCsv} />}>
        <Table cols={['Tarih', 'Gönderen', 'Konu', 'Mesaj', 'Durum']}>
          {list.map(m => (
            <tr key={m.id} className={`border-t border-ink-50 cursor-pointer hover:bg-cream-200 ${!m.read ? 'bg-gold/5' : ''}`} onClick={() => { setOpen(m); if (!m.read) markRead(m.id); }}>
              <td className="p-3 text-xs tabular text-ink-500">{fmtDate(m.created_at)}</td>
              <td className="p-3 text-sm font-semibold">{m.name}<br /><span className="text-xs text-ink-400 font-normal">{m.email}</span></td>
              <td className="p-3 text-sm">{m.subject || '-'}</td>
              <td className="p-3 text-xs text-ink-500 max-w-md truncate">{m.message}</td>
              <td className="p-3">{m.read ? <Badge tone="emerald">Okundu</Badge> : <Badge tone="coral">Yeni</Badge>}</td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-ink-400">Henüz mesaj yok</td></tr>}
        </Table>
      </Section>
      {open && (
        <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Mesaj</div>
            <h2 className="font-display text-2xl mt-1">{open.subject || '(konu yok)'}</h2>
            <div className="mt-3 text-sm text-ink-500">
              <span className="font-semibold text-ink-600">{open.name}</span> · {open.email}
            </div>
            <div className="mt-1 text-xs text-ink-400 tabular">{fmtDate(open.created_at)}</div>
            <div className="mt-5 p-4 bg-cream-200 rounded-xl whitespace-pre-wrap text-sm">{open.message}</div>
            <div className="mt-5 flex justify-end gap-2">
              <a href={`mailto:${open.email}`} className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold inline-flex items-center gap-2"><Mail className="w-4 h-4" /> E-posta ile yanıtla</a>
              <button onClick={() => setOpen(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 text-sm font-semibold">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, kicker, subtitle, action, children }) {
  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">{kicker}</div>
          <h1 className="font-display text-4xl mt-2 leading-none">{title}</h1>
          <p className="mt-2 text-ink-400 text-sm">{subtitle}</p>
        </div>
        {action}
      </div>
      <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">{children}</div>
    </div>
  );
}

function Table({ cols, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-cream-200">
          <tr>{cols.map(c => <th key={c} className="text-left p-3 font-semibold text-ink-500 text-xs uppercase tracking-wider whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Badge({ tone, children }) {
  const t = { gold: 'bg-gold/15 text-gold-500', navy: 'bg-ink-50 text-ink-600', emerald: 'bg-emerald-50 text-emerald-600', coral: 'bg-coral-50 text-coral-300' }[tone] || 'bg-ink-50 text-ink-600';
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${t}`}>{children}</span>;
}

function statusTone(s) {
  return s === 'approved' ? 'emerald' : s === 'rejected' ? 'coral' : s === 'reviewing' ? 'gold' : 'navy';
}
